/**
 * Atom declarations for a ControlSurface — the typed shapes that describe a
 * surface's state.
 *
 *  - `SurfaceAtomDeclaration` — internal atom (no agent metadata).
 *  - `PublicSurfaceAtomDeclaration` — agent-visible atom (required name + description).
 *  - `DerivedAtomDeclaration` — a Jotai derived atom expressed as get/set over
 *    already-materialised upstream atoms.
 *  - `LazyAtomMapDeclaration` — a per-key atom map created on demand.
 *
 * Declarations are materialised into live atoms by `materialiseSurfaceAtoms`
 * (see `materialiseSurfaceAtoms.ts`); accessed with full type inference via the
 * `useSurfaceAtoms` family.
 */

import type { RequiredLocalStateOptions } from "@/components/ec/endlaTypes";
import type { Getter, Setter } from "jotai";
import type { z } from "zod";

/**
 * Override the inferred TypeScript type of a Zod schema. Validates
 * bidirectional assignability: T must extend the schema output AND
 * the schema output must extend T. Use when Zod infers the correct
 * shape but you want a named type.
 *
 * Usage: `typed<MyType>()(schema)` — curried to avoid partial generic inference.
 */
export function typed<T>() {
  return <S extends z.ZodType>(
    schema: S & ([z.infer<S>] extends [T] ? unknown : never)
  ): z.ZodType<T> => schema as z.ZodType<T>;
}

/**
 * Storage strategy for a plain surface atom — a durability scale. Each value is a
 * surface-level alias of a `$`-builder leaf `mode` (the two vocabularies are kept
 * distinct, so no persistence value shares a name with its mode):
 *  - `"state"` → `mode: "tracked"`   — saved to the document (file + undo).
 *  - `"kept"`  → `mode: "session"`   — kept for the session, not saved (in the
 *    path-store, no undo, no file). Lives in the `local.state` bucket like `"state"`.
 *  - `"none"`  → `mode: "transient"` — ephemeral (resets every mount).
 */
export type AtomPersistence = "state" | "kept" | "none";

/**
 * Atom declaration shape used by `createSurfaceAtom`. Shared by
 * `SurfaceAtomDeclaration` and `PublicSurfaceAtomDeclaration`.
 */
export type AtomCreationDescriptor<T = unknown> = {
  id: string;
  schema: z.ZodType<T>;
  defaultValue: T | ((options: RequiredLocalStateOptions) => T);
  persistence: AtomPersistence;
  /**
   * When true, a persisted value is deep-merged over the default instead of
   * replacing it — mirrors `$.atom(..., { mergeDefaults: true })`. Use when the
   * save path can drop fields (e.g. `stripEmpty` removing empty arrays) and the
   * default must back-fill the gaps on load.
   */
  mergeDefaults?: boolean;
  /**
   * Round-trip the persisted value through `atomWithJsonCodec` (the default JSON
   * codec) instead of the path-store's structural serialization — mirrors the
   * `$.atomWithJsonCodec(...)` leaf. Use for values a plain round-trip would
   * corrupt: `Set`, `Map`, `Vector3`, class instances. Only meaningful for
   * persisted atoms (`"state"` / `"kept"`).
   */
  jsonCodec?: boolean;
};

/**
 * Lightweight atom declaration for surface-internal atoms.
 *
 * Carries no agent-facing metadata (name, description). It exists purely to
 * declare atoms that the surface hook manages internally — the surface hook
 * itself decides what to expose publicly.
 *
 * Created by `getWithLocalStateHOC` via `$.atom`, placed at
 * `local.state[id]` or `local.none[id]`.
 *
 * Use `useInternalSurfaceAtoms(surface)` to access with full type inference.
 */
export type SurfaceAtomDeclaration<
  TId extends string = string,
  TSchema extends z.ZodType = z.ZodType,
> = {
  /** Atom key in local.state or local.none. */
  id: TId;
  /** Zod schema for runtime validation and saved-state parsing. */
  schema: TSchema;
  /** Static default or factory receiving EC options. */
  defaultValue: z.infer<TSchema> | ((options: RequiredLocalStateOptions) => z.infer<TSchema>);
  /** Storage strategy: `"state"` (saved) | `"kept"` (session, unsaved) | `"none"` (ephemeral). */
  persistence: AtomPersistence;
  /**
   * When true, a persisted value is deep-merged over the default instead of
   * replacing it — mirrors `$.atom(..., { mergeDefaults: true })`. Use when the
   * save path can drop fields (e.g. `stripEmpty` removing empty arrays) and the
   * default must back-fill the gaps on load.
   */
  mergeDefaults?: boolean;
  /**
   * Round-trip the persisted value through `atomWithJsonCodec` (the default JSON
   * codec) instead of the path-store's structural serialization — mirrors the
   * `$.atomWithJsonCodec(...)` leaf. Use for values a plain round-trip would
   * corrupt: `Set`, `Map`, `Vector3`, class instances. Only meaningful for
   * persisted atoms (`"state"` / `"kept"`).
   */
  jsonCodec?: boolean;
};

/**
 * Agent-visible atom declaration — extends `SurfaceAtomDeclaration` with
 * required `name` and `description` for agent discovery.
 *
 * Atoms on `publicAtoms[]` are auto-registered by `LocalStateSurfaceMounter`
 * into the ControlSurface registry, making them discoverable via
 * `ec_surface_read` / `ec_surface_write`.
 *
 * TypeScript enforces that `name` and `description` are required — unlike a
 * boolean flag approach, forgetting them is a compile error.
 */
export type PublicSurfaceAtomDeclaration<
  TId extends string = string,
  TSchema extends z.ZodType = z.ZodType,
> = SurfaceAtomDeclaration<TId, TSchema> & {
  /** Human-readable label (required for agent discovery). */
  name: string;
  /**
   * Agent-facing description: what this atom controls, valid values, and
   * typical usage. Surfaced by ec_surface_read.
   */
  description: string;
};

/**
 * Declarative derived atom on a surface. The framework creates one Jotai
 * derived atom via `atom(decl.get, decl.set)` and stores it under `decl.id`
 * in the surface's `state` bucket alongside the surface's other atoms.
 *
 * The `derivedAtoms` field on a surface is a callback that closes over the
 * already-materialised upstream atoms — so a derived atom's `get` / `set`
 * can reference upstream atoms by reference (fully typed, refactor-safe),
 * not by string id.
 *
 * ```ts
 * derivedAtoms: ({ atoms }) => [
 *   {
 *     id: "myDerivedAtom",
 *     get: (get) => transform(get(atoms.myStoredAtom)),
 *     set: (get, set, next) => set(atoms.myStoredAtom, untransform(next)),
 *   },
 * ]
 * ```
 *
 * No WeakMap caching — single materialisation at mount gives identity stability.
 */
export type DerivedAtomDeclaration<T = unknown, TId extends string = string> = {
  /** Atom key in `local.state[id]` (or `scope.dataState[id]` for scope surfaces). */
  id: TId;
  /** Reactive read — runs inside the derived atom's getter. */
  get: (get: Getter) => T;
  /** Reactive write — invoked when callers set the derived atom. Omit for read-only views. */
  set?: (get: Getter, set: Setter, value: T) => void;
};

/**
 * Identity helper that preserves the literal `id` type and the inferred value
 * type of a derived-atom declaration. Use inside a surface's `derivedAtoms`
 * callback so consumer-side hooks (`useScopeSurfaceDerivedAtoms`,
 * `useSurfaceDerivedAtoms`) can infer a record keyed by the actual id
 * (`{ scenePathFindingSettingsAtom: EndlaAtom<...> }`) instead of falling back
 * to `Record<string, EndlaAtom<unknown>>`.
 *
 * ```ts
 * derivedAtoms: ({ storedAtom }) => [
 *   defineDerivedAtom({
 *     id: "myDerivedAtom",
 *     get: (get) => transform(get(storedAtom)),
 *     set: (get, set, next) => set(storedAtom, untransform(next)),
 *   }),
 * ],
 * ```
 */
export function defineDerivedAtom<const TId extends string, T>(
  decl: DerivedAtomDeclaration<T, TId>
): DerivedAtomDeclaration<T, TId> {
  return decl;
}

/**
 * Declares a lazy atom map — a `LazyAtomsMapWithRecord<K, V>` created on
 * demand per key. `materialiseSurfaceAtoms` builds each entry via
 * `$.atomMap(...)` so per-key atoms are path-store-tracked and accept
 * `RESET`; the map's `.toRecordAtom()` aggregates all keys for agent
 * read-out.
 *
 * When `public` is set, `LocalStateSurfaceMounter` registers a derived
 * `.toRecordAtom()` so agents can read the whole map as a JSON object
 * via `ec_get_config`.
 */
export type LazyAtomMapDeclaration<
  TId extends string = string,
  TKeySchema extends z.ZodType = z.ZodType,
  TValueSchema extends z.ZodType = z.ZodType,
> = {
  /** Atom key in `local.state` or `local.none`. */
  id: TId;
  /** Zod schema for the MAP KEY (used for agent docs, not runtime validation). */
  keySchema: TKeySchema;
  /** Zod schema for the per-key VALUE (used for agent docs + saved-state parsing). */
  valueSchema: TValueSchema;
  /**
   * Default value for a newly-created key. Omit to let missing keys resolve
   * to `null` — the inferred value type widens to `z.infer<valueSchema> | null`.
   */
  defaultValue?: unknown;
  /** "state" = persisted to backend; "none" = ephemeral. */
  persistence: "state" | "none";
  /**
   * Materialise the per-key atoms in the untracked `transient` slice instead of
   * the path-store (`tracked`/`session`). Use for runtime data caches that must
   * not be undoable or serialized — e.g. fetched track data keyed by track id.
   * Implied when `atomFunc` is set (it is incompatible with store-backed modes).
   */
  transient?: boolean;
  /**
   * Per-key atom constructor — mirrors `$.atomMap(..., { atomFunc })`. Lets a
   * map vend a specialised atom (e.g. `atomWithCompare` for referential
   * stability). Only valid for `transient` maps; forces `transient` mode.
   */
  atomFunc?: (key: z.infer<TKeySchema>) => (init: z.infer<TValueSchema>) => unknown;
  /** When set, the `.toRecordAtom()` is registered as an agent-visible entry. */
  public?: {
    name: string;
    description: string;
  };
};
